<script>
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'
import { formatIndexDate } from '@/helpers/tzDate'
import McCheckbox from '@/components/McCheckbox'

export default {
  components: {
    McButton,
    McCheckbox,
  },
  mixins: [Helper],
  props: {
    fields: { required: true },
    buttons: { required: false },
    apiClient: { required: true },
    trackBy: { default: 'id' },
    limitOptions: {
      default: () => [10, 25, 50, 100],
    },
    searchable: { type: Boolean },
    busy: { type: Boolean },
    tableTitle: { type: String },
    withCard: { type: Boolean },
    disabled: { type: Boolean },
    showNoData: { type: Boolean },
    formErrors: {},
    listedDeletedItems: { type: Boolean },
    deletedItemsKey: { type: String },
    secondAlertText: { type: String },
    showPagination: { type: Boolean, default: true },
    canCreate: { type: Boolean, default: true },
    canDelete: { type: Boolean, default: true },
    canSelect: { type: Boolean, default: true },
    isPredefined: { type: Boolean, default: false },
    fieldException: { type: Array, required: false },
    customToast: { type: Function },
    idIsString: { default: false },
    trackStateBy: { type: String },
    isEmpty: {
      type: Boolean,
    },
    addCustom: {
      type: Object,
    },
    singleSort: {
      type: Boolean,
      default: false,
    },
    hasMassSelect: {
      type: Boolean,
      default: false,
    },
    withFlex: {
      type: Boolean,
      default: true,
    },
    hideCheckbox: {
      type: Boolean,
      default: false,
    },
    hasViewManager: {
      type: Boolean,
      default: false,
    },
    isResponsive: {
      type: Boolean,
      default: true,
    },
    hasStickyAction: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    isAllEditable: {
      type: Boolean,
      default: true,
    },
    hasCustomAction: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      page: 1,
      currentPage: 1,
      limit: this.limitOptions[0] ?? 10,
      totalCount: 0,
      isBusy: true,
      search: '',
      sorts: [],
      items: [],
      ids: [],
      rowUpdate: {},
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      selectedIds: {},
      selectedIdsLength: 0,
      selectedListName: [],
      selectedAll: false,
      formValues: {},
      fullObject: {},
      countWithoutCheckbox: 0,
      isRetrying: false,
      dataFields: this.cloneFields(this.fields),
      dataColumns: this.cloneColumns(this.fields),
      initialFields: this.cloneFields(this.fields),
    }
  },

  computed: {
    locale() {
      return this.$i18n.locale
    },
    disabledGlobal() {
      return this.disabled || this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    // Fields with added defaults
    computedFields() {
      let fields = this.dataFields?.filter((field) => field.shown)
      if (!this.hideCheckbox) {
        fields.unshift({
          key: 'checkbox',
          label: '',
        })
      }
      if (this.canEdit) {
        fields.push({
          key: '__action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        })
      }
      return fields
    },
  },

  watch: {
    async locale() {
      this.dataFields = this.cloneFields(this.fields)
    },
  },

  async mounted() {
    if (this.showNoData) {
      this.isBusy = false
      return
    }

    this.fields.forEach((el) => {
      if (el.isSortable) el._sort = 'desc'
      if (el.orderBy) this.sorts.push({ [el.key]: el.orderBy })
    })

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
      sorts: this.stringSorts(),
    })
  },

  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },

    formatIndexDate,
    stringSorts() {
      const arr = this.sorts.map((sort) => {
        const obj = {
          [sort.key]: sort.order,
        }
        return JSON.stringify(obj)
      })

      return arr
    },
    transformCellValue(value) {
      if (value === false) return ''
      if (typeof value === 'number') return value.toLocaleString('id-ID')
      return value
    },
    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    // TECHDEBT: Only supports resourceful format
    // getData methods can be improved to support user-supplied data
    async getData(opts) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      const filter = {
        pagination: {
          page: opts.page,
          show: opts.limit,
        },
        orderBy: opts.sorts,
        search: opts.search,
      }

      if (filter?.orderBy && !filter.orderBy.length) delete filter.orderBy
      if (filter?.search && !filter.search) delete filter.search

      this.isBusy = true
      const res = await this.apiClient.get(filter)
      if (!res) return
      this.items = res.data
      this.page = res.pagination.page
      this.totalCount = res.pagination.total
      this.countWithoutCheckbox = res.data.filter((x) => x.isShowCheckbox == false).length

      const getIds = (datas) => {
        return datas
          .filter(
            (x) =>
              (x.isShowCheckbox === undefined && !x.disableCheckbox) ||
              (x.disableCheckbox === undefined && x.isShowCheckbox)
          )
          .map((x) => x[this.trackBy])
      }

      if (this.hasMassSelect && !opts.search) {
        const allFilter = {
          pagination: {
            page: 1,
            show: 9999,
          },
          search: opts.search,
        }
        const allRes = await this.apiClient.get(allFilter)
        this.ids = getIds(allRes.data)
      } else this.ids = getIds(this.items)

      if (res.data.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }
      this.isBusy = false
    },

    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
      this.$refs['searchInput'].$el.focus()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
      this.search = ''
    },

    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      })
    },

    async handlePageChange(newPage) {
      if (!this.hasMassSelect) {
        this.handleUnselectAll()
      }
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      let newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // asc - desc (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          // If it have single sort, remove all other sorts
          if (this.singleSort) newSorts = []
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]

      await this.$nextTick()

      return this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.stringSorts(),
      })
    },
    async handleEditStart(data) {
      if (this.disabled) return
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return
      // refetch single data from backend for data integrity
      const refetchedData = data.item
      if (!refetchedData) return
      if (this.isPredefined && data.item.companyId == 1) {
        this.dataFields.forEach((item) => {
          if (!this.fieldException || !this.fieldException.includes(item.key)) {
            item.isEditable = false
          }
        })
      } else if (this.isPredefined) {
        this.dataFields.forEach((item) => {
          item.isEditable = item.editable !== false
        })
      }
      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item[this.trackBy] == data.item[this.trackBy]) return refetchedData
        return item
      })

      this.fullObject = {}
      this.dataFields.forEach((field) => {
        if (field.type === 'select-resourceful-v3' && field.name) {
          this.$set(this.fullObject, field.name, field.getInitialObject?.(refetchedData))
        }
      })

      this.isEditingRow = data.index
      await this.$nextTick()
      this.$emit('changeData')
      this.$emit('handleChange', data)
      this.$refs[`${this.dataFields[0].key}-ref`]?.$el.querySelector('input').focus()
    },
    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }
      this.isEditingRow = null
      this.fullObject = {}
      this.$emit('handleEditCancel')
    },
    // TECHDEBT: transformFields will be on formulate input instead of here
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: typeof value === 'string' ? value.trim() : value }
        })
      )
    },
    async handleSubmit(data) {
      const validationResult = await new Promise((resolve) => {
        this.$emit('beforeSubmit', data, this.items[this.isEditingRow], resolve)
        // If no listener, resolve immediately with success
        if (!this.$listeners.beforeSubmit) {
          resolve({ success: true })
        }
      })

      if (validationResult && validationResult.success === false) {
        return false
      }

      const path = window.location.pathname
      const hasRfidId = path.match(/\/rfid\/(\d+)$/)
      if (hasRfidId) {
        this.$emit('handleSubmit', data, this.items[this.isEditingRow])
        this.isEditingRow = null
        this.isAdding = false
        this.isSubmitting = false
        this.isBusy = false
        this.isRetrying = false
        return
      }

      if (!this.$listeners.beforeSubmit) {
        let dataKey = Object.keys(data)
        if (dataKey.length > 1) {
          dataKey = dataKey[0]
        }
        const hasIbeacon = path.includes('ibeacon')
        if (!data[dataKey] && !hasIbeacon) {
          return false
        }
      }

      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      this.isSubmitting = true

      // Cast fields to correct types
      const transformedData = this.transformFields(data, this.dataFields)

      let newData = {}

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }

      if (!newData) {
        this.isSubmitting = false
        this.formValues = data

        return
      }

      // Replace data on the table
      if (this.isAdding) {
        this.items.splice(0, 1, newData)
      } else {
        let itemsTemp = [...this.items]
        this.items.splice(0, this.items.length)
        const itemsTempIndex = itemsTemp.findIndex((x) => x[this.trackBy] == newData[this.trackBy])
        itemsTemp[itemsTempIndex] = newData
        await this.$nextTick()
        this.items = itemsTemp
      }

      this.isEditingRow = null
      this.isAdding = false
      this.fullObject = {}
      this.isSubmitting = false
      this.isBusy = false
      this.isRetrying = false
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (!createdData) {
        return false
      }
      const refetchedData = createdData
      if (!refetchedData) return false

      this.ids.push(refetchedData[this.trackBy])
      this.totalCount += 1

      this.$mcToast.success(this.$t('global.alert.text.added'))

      return {
        ...refetchedData,
      }
    },
    async submitUpdate(data, oldData) {
      let patchData = oldData
      let hasChanged = null

      if (!this.isRetrying) {
        ;[patchData, hasChanged] = this.filterUnchangedData(data, oldData)
        if (!hasChanged) return null
      }

      let newData = patchData

      if (data[this.trackBy] !== oldData[this.trackBy]) newData = data

      const updatedData = await this.apiClient.update(oldData[this.trackBy], oldData, newData)
      if (!updatedData) return false

      const refetchedData = updatedData
      if (!refetchedData) return false

      this.$mcToast.success(this.$t('global.alert.text.changed'))

      await this.getData({
        limit: this.limit,
        page: this.page,
        sorts: this.stringSorts(),
      })

      return {
        ...refetchedData,
      }
    },
    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(errors) {
      return
      // return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (key == this.trackBy) return
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      this.isBusy = true
      let promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key))
        }
      }

      await Promise.all(promises).then((res) => {
        if (this.search) this.handleClearSearch()
        this.handleUnselectAll()
        setTimeout(() => {
          this.getData({
            limit: this.limit,
            page: 1,
            sorts: this.stringSorts(),
          })

          this.$refs['mc-modal-delete'].hide()

          res.forEach((data) => {
            if (data === 'HANDLED_ERROR') return
            if (!data) {
              if (this.customToast) {
                this.customToast()
              } else {
                this.$mcToast.error('Gagal dihapus')
              }
            } else {
              this.$mcToast.success('Data berhasil dihapus')
            }
            this.selectedListName = []
          })
        }, 300)
      })
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item[this.trackBy]]: checked }
      if (checked) {
        this.selectedIdsLength += 1

        const selectableItems = this.items.filter(
          (item) => !(item.disableCheckbox || this.isRowOnProgress(item) || this.isEmptyState(item))
        )
        if (this.selectedIdsLength === selectableItems.length) {
          this.selectedAll = true
        }

        if (this.listedDeletedItems) {
          this.selectedListName = {
            ...this.selectedListName,
            [data.item[this.trackBy]]: data.item[this.deletedItemsKey],
          }
        }
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1

        if (this.listedDeletedItems) {
          delete this.selectedListName[data.item[this.trackBy]]
        }
      }
      this.$emit('handleSelectValue', this.selectedIds)
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(
          this.ids
            .filter((id) => {
              const item = this.items.find((item) => item[this.trackBy] === id)
              return !(
                item?.disableCheckbox ||
                this.isRowOnProgress(item) ||
                this.isEmptyState(item) ||
                this.isRowSuccess(item)
              )
            })
            .map((id) => [id, true])
        )
        this.selectedIdsLength = Object.keys(this.selectedIds).length
        this.selectedAll = true
        this.$emit('handleSelectValue', this.selectedIds)
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
      this.$emit('handleSelectValue', {})
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      if (this.isAllEditable) {
        this.dataFields.forEach((item) => {
          item.isEditable = true
        })
      }

      this.dataFields.forEach((item) => {
        item.isEditable = item.editable !== false
      })
      this.$emit('available-change', true)
      this.$emit('changeData')

      if (this.addCustom && this.items.length === 0) {
        this.$emit('addData')
        this.items = [this.addCustom, ...this.items]
      } else {
        this.items = [{ [this.trackBy]: this.idIsString ? '' : 0 }, ...this.items]
      }
    },
    async handleInput(field, item, value) {
      await this.$nextTick()
      const index = this.items.findIndex((x) => x[this.trackBy] == item[this.trackBy])
      this.$emit('handleInput', index, item, value)
    },
    getValidation(field, item) {
      if (!field.validation) return undefined
      if (!Array.isArray(field.validation)) return field.validation

      return field.validation.map((rule) => {
        if (Array.isArray(rule) && rule[0] === 'isUnique') {
          // If we have an ID, pass it as second arg to isUnique to exclude it
          if (item && item[this.trackBy]) {
            return ['isUnique', rule[1], item[this.trackBy], this.trackBy]
          }
        }
        return rule
      })
    },
    getCountryCode(value) {
      this.$emit('countryCode', value)
    },
    isEmptyState(item) {
      if (this.trackStateBy) {
        return !item[this.trackStateBy]
      }
      return false
    },
    isRowSuccess(item) {
      const rfidField = this.fields.find((field) => field.key === 'rfid')
      if (rfidField && item?.rfidMaster === null) {
        return true
      }
    },
    isRowOnProgress(item) {
      // P = Progress
      // S = Success
      // F = Failed
      if (!this.trackStateBy || !item[this.trackStateBy]) return false
      return item[this.trackStateBy] === 'P'
    },
    isRowFailed(item) {
      if (!this.trackStateBy || !item[this.trackStateBy]) return false
      return item[this.trackStateBy] === 'F'
    },
    handleRetry(data) {
      this.isRetrying = true
      this.isEditingRow = data.index
      return this.handleSubmit(data.item)
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    cloneFields(fields) {
      const finalFields = this.deepClone(fields, 'child')
      finalFields.forEach((item) => {
        if (item.shown === undefined) item.shown = true
      })
      return finalFields
    },

    cloneColumns(fields) {
      const finalFields = this.deepClone(fields, 'parent')
      finalFields.forEach((item) => {
        if (item.shown === undefined) item.shown = true
      })
      return finalFields
    },

    deepClone(fields, level) {
      const finalFields = []
      fields.forEach((field) => {
        if (level == 'parent') {
          finalFields.push(field)
        } else {
          if (Object.hasOwn(field, 'children')) {
            finalFields.push(...field.children)
          } else {
            finalFields.push(field)
          }
        }
      })

      return finalFields
    },

    handleSetDataFields(fields) {
      this.dataFields = this.cloneFields(fields)
      this.dataColumns = this.cloneColumns(fields)
    },
  },
}
</script>

<template>
  <div
    class="mc-table mc-editable-table"
    :class="{ 'has-checkboxes': !hideCheckbox, 'has-sticky-action': hasStickyAction }"
  >
    <div class="d-flex justify-content-between align-items-center pb-3">
      <div class="fw-bold text-nowrap card-title mb-0">
        {{ tableTitle }}
      </div>
      <div class="ml-auto d-flex justify-content-end">
        <template v-for="(btn, index) in buttons">
          <McButton
            v-if="!btn.hide"
            :id="btn.id"
            :key="`button-${index}`"
            :variant="btn.variant"
            :class="btn.class"
            :text="btn.text"
            :icon="btn.icon"
            @click="btn.handleClick"
          />
        </template>

        <McButton
          v-if="selectedIdsLength && canDelete"
          id="mc-delete-btn"
          variant="secondary-red"
          class="me-2"
          :text="$t('global.delete')"
          icon="icon-outline_trash"
          @click="$refs['mc-modal-delete'].show()"
        />

        <slot name="addButton" :handleAdd="handleAdd">
          <McButton
            v-if="canCreate"
            id="mc-add-btn"
            variant="primary"
            :disabled="disabledGlobal"
            :text="$t('global.action.create')"
            icon="icon-fill_plus"
            @click="handleAdd"
          />
        </slot>
      </div>
    </div>

    <div v-if="!isEmpty" :class="{ 'card p-3 mt-2': withCard }">
      <b-row align-h="between">
        <b-col :md="4">
          <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            {{ $t('global.object.selectedRow') }}
            <a class="color-primary" @click="handleUnselectAll">{{
              $t('global.action.deselect')
            }}</a>
          </h6>
        </b-col>
        <b-col
          :md="hasViewManager ? 8 : 4"
          :class="[
            withFlex ? 'd-flex' : '',
            'flex-row-reverse mc-table-view-wrapper',
            { 'mt-0': withCard, 'mt-2': !withCard },
          ]"
        >
          <div class="mc-table-view d-flex flex-row-reverse">
            <b-input-group v-if="searchable" size="sm" class="mc-has-search" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search"></i>
              </template>
              <b-form-input
                ref="searchInput"
                :placeholder="`${$t('global.search')}...`"
                :value="search"
                @input="handleSearchChange"
              />
              <template v-if="search" #append>
                <button
                  class="clear-search-btn"
                  :disabled="disabledGlobal"
                  type="button"
                  @click="handleClearSearch"
                >
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
            <slot
              name="viewManager"
              :handleSetDataFields="handleSetDataFields"
              :dataFields="dataFields"
              :initialFields="initialFields"
              :dataColumns="dataColumns"
            />
          </div>
        </b-col>
      </b-row>
      <FormulateForm
        ref="formRef"
        v-model="formValues"
        name="editable-table-form"
        :errors="formErrors"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <div
          :class="{
            'table-responsive my-3 border border-1 mc-table-overflow': true,
            'text-nowrap': !isResponsive,
          }"
        >
          <b-table
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :per-page="limit"
            fixed
            show-empty
            class="editable-table"
            responsive="md"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2" />
                <strong>Loading...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">
                {{ $t('global.emptyTable') }}
              </div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template v-if="!hideCheckbox" #head(checkbox)>
              <McCheckbox
                v-if="canSelect"
                v-model="selectedAll"
                :disabled="disabledGlobal || ids.length == 0"
                @change="handleSelectAll"
              />
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template v-if="!hideCheckbox" #cell(checkbox)="data">
              <McCheckbox
                v-if="data.item.isShowCheckbox !== undefined ? data.item.isShowCheckbox : true"
                v-model="selectedIds[data.item[trackBy]]"
                :disabled="
                  disabledGlobal ||
                  data.item.disableCheckbox ||
                  isRowOnProgress(data.item) ||
                  isEmptyState(data.item) ||
                  isRowSuccess(data.item)
                "
                @change="(event) => handleSelect(event, data)"
              />
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                <FormulateInput
                  v-if="field.type === 'phone-number'"
                  v-bind="{ ...field }"
                  :ref="`${field.key}-ref`"
                  :rowItem="item"
                  :label="null"
                  :name="field.inputName || field.key"
                  :type="field.type || 'text'"
                  :validation="getValidation(field, item)"
                  :value="getInputInitialValue({ field, item })"
                  :disabled="isSubmitting || field.disabled"
                  size="sm"
                  validation-behavior="blur"
                  error-behavior="blur"
                  @countryCode="getCountryCode"
                />

                <FormulateInput
                  v-else
                  v-bind="{ ...field }"
                  :ref="`${field.key}-ref`"
                  :rowItem="item"
                  :label="null"
                  :name="field.inputName || field.key"
                  :type="field.type || 'text'"
                  :validation="getValidation(field, item)"
                  :value="getInputInitialValue({ field, item })"
                  :initialObject="
                    field.type === 'select-resourceful-v3'
                      ? undefined
                      : field.getInitialObject?.(item)
                  "
                  :fullObject="
                    field.type === 'select-resourceful-v3' ? fullObject[field.name] : undefined
                  "
                  :disabled="isSubmitting"
                  validation-behavior="submit"
                  error-behavior="submit"
                  @input="(value) => handleInput(field, item, value)"
                  @update:fullObject="
                    field.type === 'select-resourceful-v3' && field.name
                      ? $set(fullObject, field.name, $event)
                      : null
                  "
                />
              </template>

              <!-- Bold First Column -->
              <template v-else-if="field.isRedirectable">
                <div class="fw-bold text-primary">
                  {{ value }}
                </div>
              </template>

              <template
                v-else-if="field.customColumn && isEditingRow !== index && $scopedSlots[field.key]"
              >
                <slot :name="field.key" v-bind="{ index, field, value, item }"></slot>
              </template>

              <template v-else-if="isEditingRow == index && $scopedSlots[`${field.key}-edit`]">
                <slot :name="`${field.key}-edit`" v-bind="{ index, field, value, item }"></slot>
              </template>

              <template v-else-if="field.type === 'date'">
                <p class="my-0">
                  {{ item ? formatIndexDate(item[field.key]).date : '-' }}
                </p>
                <p v-if="item" class="text-muted my-0">
                  <template v-if="item[field.key]">
                    {{ formatIndexDate(item[field.key]).time }}
                  </template>
                </p>
              </template>

              <template v-else-if="field.type === 'badge'">
                <div class="battery-level-container">
                  <b-alert
                    v-if="item[field.key] !== null"
                    :variant="getBatteryVariant(item[field.key])"
                    show
                    class="mb-0 py-1 px-2 d-inline-block"
                  >
                    {{ item[field.key] }}%
                  </b-alert>
                  <span v-else>-</span>
                </div>
              </template>

              <template v-else>
                {{ transformCellValue(value) }}
              </template>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template v-if="canEdit" #cell(__action)="data">
              <div class="__action-wrapper">
                <template v-if="hasCustomAction">
                  <slot name="custom-action" v-bind="data" />
                </template>
                <div v-if="data.item.isShowEdit !== undefined ? data.item.isShowEdit : true">
                  <div v-if="isEditingRow == data.index">
                    <b-button
                      variant="outline"
                      :class="`__accept-button btn ${hasCustomAction ? 'float-right ml-1' : ''}`"
                      type="submit"
                    >
                      <i class="mc-icon icon-fill_checkmark"></i>
                    </b-button>

                    <b-button
                      variant="outline"
                      class="__cancel-button btn"
                      @click="handleEditCancel(data)"
                    >
                      <i class="mc-icon icon-outline_close"></i>
                    </b-button>
                  </div>
                  <b-spinner v-else-if="isRowOnProgress(data.item)" small />
                  <template v-else>
                    <b-button
                      v-if="isRowFailed(data.item)"
                      variant="outline"
                      :class="{
                        '__retry-button btn': true,
                        __disabled: disabled || isEditingRow !== null || isBusy,
                      }"
                      @click="handleRetry(data)"
                    >
                      <i class="mc-icon icon-fill_refresh"></i>
                    </b-button>
                    <b-button
                      variant="outline"
                      :class="{
                        '__edit-button btn': true,
                        __disabled: disabled || isEditingRow !== null || isBusy,
                      }"
                      @click="handleEditStart(data)"
                    >
                      <i class="mc-icon icon-outline_edit"></i>
                    </b-button>
                  </template>
                </div>
              </div>
            </template>

            <!-- Slot: Slot override from parent component -->
            <template v-for="(index, name) in $scopedSlots" #[name]="data">
              <slot :name="name" v-bind="data"></slot>
            </template>
          </b-table>
        </div>
      </FormulateForm>
      <div v-if="showPagination" class="d-flex flex-wrap flex-md-nowrap">
        <div class="mc-limit">
          <div class="__limit">
            <div>{{ $t('global.text.display') }}</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              :value="limit"
              @input="handleLimitChange"
            />
            <div>{{ $t('global.text.entries') }}.</div>
          </div>

          <div v-if="totalCount === 0" class="__info">
            <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
            <strong>{{ totalCount }}</strong> {{ $t('global.object.data') }}.
          </div>

          <div v-else class="__info">
            {{ $t('global.text.display') }} <strong>{{ startData }}</strong>
            {{ $t('global.object.to') }} <strong>{{ endData }}</strong>
            {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
            {{ $t('global.text.entries') }}.
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            :value="page"
            @change="handlePageChange"
          />
        </div>
      </div>
    </div>

    <div v-else-if="isEmpty" :class="{ 'card p-3 mt-2': withCard }">
      <slot name="emptyState" />
    </div>

    <b-modal
      id="mc-modal-delete"
      ref="mc-modal-delete"
      class="mc-modal"
      title="Hapus"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
        <b-list-group v-if="listedDeletedItems" class="mc-delete-list">
          <b-list-group-item v-for="(item, key) in selectedListName" :key="key">
            {{ item }}
          </b-list-group-item>
        </b-list-group>
        <p v-if="secondAlertText" class="mt-3">
          {{ secondAlertText }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
@import './style.scss';
</style>
